import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { ISO_3166_1_CODES } from '@app/modules/authentication/multifactor-authentication/country-codes';
import { PhoneErrorMatcher, phoneValidator } from '@app/modules/authentication/multifactor-authentication/multifactor-authentication.component';
import { UserService } from '@app/shared/services';
import PhoneNumber from 'awesome-phonenumber';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { Clipboard } from '@angular/cdk/clipboard';
import { environment } from '@env/environment';
import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';
@AutoUnsubscribe()
@Component({
  selector: 'discovery-link',
  templateUrl: './link.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiscoveryLinkComponent implements OnInit, OnDestroy {
  @Input() data;
  @Input() hideDivider = false;
  @Input() mobileDeliverPortal = false;

  @Output() moveNextPreviousEvent = new EventEmitter<any>();

  filteredCountryCodes = [];
  isCollectionLinkSent = false;
  searchStringCode: FormControl;
  countryCodes = ISO_3166_1_CODES;
  phoneErrorMatcher = new PhoneErrorMatcher();
  collectionLink = `${environment.baseUrl}?url=mobiledelivery`;
  subscription = new Subscription();

  collectionLinkForm = this.fb.group({
    phone: this.fb.group(
      {
        country: ['US'],
        number: [''],
      },
      { validators: phoneValidator }
    ),
  });

  constructor(
    private _userService: UserService,
    private fb: UntypedFormBuilder,
    public clipboard: Clipboard,
    private toastr: ToastrService,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.searchStringCode = new FormControl();

    this.subscription.add(
      this.searchStringCode.valueChanges
        .pipe(
          startWith(''),
          map((value) => this.filterCode(value))
        )
        .subscribe((response) => {
          this.filteredCountryCodes = response;
          this.cd.detectChanges();
        })
    );
  }

  get phoneGroup() {
    return this.collectionLinkForm.get('phone') as UntypedFormControl;
  }

  get phoneCountryControl() {
    return this.collectionLinkForm.get('phone.country') as UntypedFormControl;
  }

  get phoneNumberControl() {
    return this.collectionLinkForm.get('phone.number') as UntypedFormControl;
  }

  get phoneNumberDigits(): string {
    return this.phoneNumberControl.value.replace(/\D/g, '');
  }

  get phoneNumber(): PhoneNumber {
    return new PhoneNumber(this.phoneNumberDigits, this.phoneCountryControl.value);
  }

  get phoneHint(): string {
    return PhoneNumber.getExample(this.phoneCountryControl.value).getNumber('national');
  }

  get phoneE164(): string {
    return this.phoneNumber.getNumber('e164');
  }

  sendCollectionLink() {
    if (!this.collectionLinkForm.valid) return;
    let phone_number = this.phoneNumber.getNumber();

    const payload = {
      country: this.phoneCountryControl?.value,
      phone_no: phone_number,
      collectionLink: this.collectionLink,
    };

    this.subscription.add(
      this._userService.sendCollectionLink(payload).subscribe(
        (data) => {
          this.isCollectionLinkSent = true;
          this.toastr.success(`Link sent to ${this.collectionLinkForm.get('phone.number').value}`);
          this.cd.detectChanges();
        },
        (error: any) => {
          this.toastr.error(error);
        }
      )
    );
  }

  formatNumber() {
    this.isCollectionLinkSent = false;
    const natNum = this.phoneNumber.getNumber('national');
    this.phoneNumberControl.setValue(natNum ? natNum : this.phoneNumberDigits);
  }

  private filterCode(value) {
    if (this.countryCodes) {
      if (value == null || value == '') {
        return this.countryCodes;
      } else {
        return this.countryCodes.filter((c) => c.country.toLowerCase().includes(value.toLowerCase()));
      }
    }
  }

  copyCollectionLink(value) {
    this.clipboard.copy(value);
    this.toastr.success('Link copied');
  }

  handleSteps(event) {
    this.moveNextPreviousEvent.emit({ status: event, ...(event == 'forward' && { id: this.data?.id }) });
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }
}
